import React, { useState } from 'react';

import { getOnboardingProfile } from '../../../../api/v2/program_insights';
import useProgramInsightsMetric from '../use_program_insights_metric';
import MetricCard from '../metric_card';
import styles from './index.module.css';

const DESCRIPTION =
  'Who signs up and what they want from a mentor, split by whether their first mentor message was ' +
  'before or after study consent. Age is derived from date of birth; motivations come from the ' +
  '“What are you hoping to gain from having a mentor?” question, which is multi-select — so response ' +
  'counts do not sum to the number of participants answering (N). Retention-only studies show the ' +
  'total program only.';

// view -> the field each distribution is read from in the response.
const VIEW_FIELD = {
  total: 'total',
  pre: 'pre_consent',
  post: 'post_consent',
};

const VIEWS = [
  { id: 'total', label: 'Total' },
  { id: 'pre', label: 'Pre-consent' },
  { id: 'post', label: 'Post-consent' },
];

function Bars({ rows, color }) {
  const max = Math.max(...rows.map((row) => row.count), 1);

  return (
    <ul className={styles.bars}>
      {rows.map((row) => (
        <li key={row.key} className={styles.bar}>
          <span className={styles.barLabel}>{row.label}</span>
          <div className={styles.track}>
            <div
              className={styles.fill}
              style={{
                width: `${(row.count / max) * 100}%`,
                backgroundColor: color,
                // Ensure zero-count rows render as empty by removing the min-width.
                minWidth: row.count > 0 ? 2 : 0,
              }}
            />
          </div>
          <span className={styles.barValue}>{row.count.toLocaleString()}</span>
        </li>
      ))}
    </ul>
  );
}

export default function OnboardingProfile({ filters }) {
  const { data, status } = useProgramInsightsMetric(getOnboardingProfile, filters);
  const [view, setView] = useState('total');

  const split = data && data.recruitment_and_retention;
  const activeView = split ? view : 'total';
  const field = VIEW_FIELD[activeView];

  const age = data && data.age[field];
  const motivations = data && data.motivations[field];
  const hasData =
    (age && age.some((band) => band.count > 0)) ||
    (motivations && typeof motivations.respondents === 'number' && motivations.respondents > 0);

  return (
    <MetricCard
      title='Participant Onboarding Profile'
      subtitle='First mentor message before or after study consent.'
      description={DESCRIPTION}
    >
      {status === 'loading' && <p className={styles.state}>Loading…</p>}
      {status === 'error' && <p className={styles.stateError}>We could not load this metric. Try again shortly.</p>}

      {status === 'loaded' && split && (
        <div className={styles.toggle} role='group' aria-label='Onboarding profile view'>
          {VIEWS.map((option) => (
            <button
              key={option.id}
              type='button'
              aria-pressed={activeView === option.id}
              className={`${styles.toggleButton} ${activeView === option.id ? styles.toggleButtonActive : ''}`}
              onClick={() => setView(option.id)}
            >
              {option.label}
            </button>
          ))}
        </div>
      )}

      {status === 'loaded' && !hasData && (
        <p className={styles.state}>No participants match the current filters yet.</p>
      )}

      {status === 'loaded' && hasData && (
        <>
          {activeView === 'total' && data.insight && <p className={styles.insight}>{data.insight}</p>}

          <div className={styles.columns}>
            <section className={styles.column}>
              <p className={styles.sectionLabel}>Age distribution</p>
              <Bars rows={age} color='var(--pp-teal-dark)' />
            </section>

            <section className={styles.column}>
              <p className={styles.sectionLabel}>“What are you hoping to gain from having a mentor?”</p>
              {motivations.options.length > 0 ? (
                <Bars rows={motivations.options} color='var(--pp-fresh-mint)' />
              ) : (
                <p className={styles.state}>No motivations recorded yet.</p>
              )}
            </section>
          </div>

          <p className={styles.footnote}>
            N = {motivations.respondents.toLocaleString()} participants answering. Because participants can select
            multiple motivations, response counts do not sum to N.
          </p>
        </>
      )}
    </MetricCard>
  );
}
